import React, { useState } from "react";
import { Users, Check, Loader2, LogOut, LogIn } from "lucide-react";
import {
  Dialog,
  DialogContent,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { useAcceptInvite, useRejectInvite, useInviteDetails } from "../../hooks/useFamily";
import { useGluceraAuth } from "../../hooks/useGluceraAuth";
import { useNavigate } from "react-router-dom";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";

/** localStorage key used to carry an invite token across the login/sign-up flow
 *  so an unauthenticated invitee can finish accepting after they sign in.
 *  Must match the key read in GluceraLayout. */
export const PENDING_INVITE_TOKEN_KEY = "glucera_pending_invite_token";

interface InviteAcceptModalProps {
  inviteToken: string | null;
  isOpen: boolean;
  onClose: () => void;
}

const InviteAcceptModal: React.FC<InviteAcceptModalProps> = ({
  inviteToken,
  isOpen,
  onClose,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const { logout, isAuthenticated, loading: authLoading } = useGluceraAuth();
  const navigate = useNavigate();

  // Unauthenticated invitees can't hit the accept/reject API. Once auth state is
  // resolved and the user is logged out, we redirect them through sign-in first.
  const needsAuth = !authLoading && !isAuthenticated;

  // Fetch invite details
  const {
    data: inviteDetails,
    isLoading: loadingDetails,
    error: detailsError,
  } = useInviteDetails(inviteToken || "", {
    enabled: isOpen && Boolean(inviteToken) && isAuthenticated && !authLoading,
  });

  // Mutations
  const acceptInviteMutation = useAcceptInvite();
  const rejectInviteMutation = useRejectInvite();

  const handleLogout = () => {
    logout();
    onClose();
  };

  // Persist the token and send the user to sign in; after auth, GluceraLayout
  // re-opens this modal so they can finish accepting.
  const handleSignInToAccept = () => {
    if (inviteToken) {
      try {
        localStorage.setItem(PENDING_INVITE_TOKEN_KEY, inviteToken);
      } catch {
        // ignore storage failures — user can re-open the invite link
      }
    }
    onClose();
    navigate(`/glucera${GluceraNavigationRoutes.ONBOARDING_WELCOME}`, {
      replace: true,
    });
  };

  const handleAccept = async () => {
    if (!inviteToken) return;

    // Not signed in → route through sign-in instead of calling the API (which
    // would fail with a generic error for an unauthenticated request).
    if (needsAuth) {
      handleSignInToAccept();
      return;
    }

    setIsProcessing(true);
    try {
      const response = await acceptInviteMutation.mutateAsync(inviteToken);
      
      // Success message is shown by the hook
      console.log("Joined family group:", response.group_id);
      
      // Close modal and redirect to home page
      setTimeout(() => {
        onClose();
        // Navigate to home page to see updated family list
        navigate(`/glucera${GluceraNavigationRoutes.HOMEPAGE}`, { replace: true });
      }, 1500);
    } catch (error: any) {
      console.error("Error accepting invite:", error);
      
      // Check error code and handle accordingly
      const errorCode = error?.response?.data?.error?.code;
      if (errorCode === "email_mismatch") {
        // Error message already shown by the hook
        // Don't close modal, let user read the message
        setIsProcessing(false);
      } else if (errorCode === "cannot_accept_own_group") {
        // User tried to accept their own invite - close modal with message
        setIsProcessing(false);
        setTimeout(() => {
          onClose();
        }, 3000);
      } else if (errorCode === "invalid_token" || errorCode === "invite_expired") {
        // Token invalid - close modal after showing error
        setIsProcessing(false);
        setTimeout(() => {
          onClose();
        }, 3000);
      } else {
        // For other errors, close modal after delay
        setTimeout(() => {
          onClose();
        }, 2000);
      }
    } finally {
      if (!acceptInviteMutation.isError || 
          acceptInviteMutation.error?.response?.data?.error?.code !== "email_mismatch") {
        setIsProcessing(false);
      }
    }
  };

  const handleReject = async () => {
    if (!inviteToken) return;

    // Not signed in → just dismiss; there's no session to reject against.
    if (needsAuth) {
      try {
        localStorage.removeItem(PENDING_INVITE_TOKEN_KEY);
      } catch {
        // ignore
      }
      onClose();
      return;
    }

    setIsProcessing(true);
    try {
      await rejectInviteMutation.mutateAsync({ token: inviteToken });
      // Success message is shown by the hook
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      console.error("Error rejecting invite:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Show loading state
  if (loadingDetails) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-[95%] sm:max-w-md">
          <div className="flex flex-col items-center justify-center py-6 sm:py-8">
            <Loader2 className="h-10 w-10 sm:h-12 sm:w-12 animate-spin text-violet-600 mb-3 sm:mb-4" />
            <p className="text-xs sm:text-sm text-muted-foreground">Loading invite details...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95%] sm:max-w-md p-0 gap-0 overflow-hidden">
        {/* Header with gradient */}
        <div className="bg-gradient-to-br from-violet-600 to-purple-600 px-5 py-6 text-center">
          <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
            <Users className="h-8 w-8 text-white" strokeWidth={2} />
          </div>
          <h2 className="text-lg font-bold text-white">You're Invited!</h2>
          <p className="mt-1 text-sm text-violet-100">
            Someone wants you to join their Family Health Circle
          </p>
        </div>

        <div className="space-y-4 px-5 py-4 pb-5">
          {/* What you'll track together */}
          <div className="rounded-xl border border-violet-100 dark:border-violet-900 bg-violet-50/50 dark:bg-violet-950/20 p-3.5">
            <p className="text-xs font-semibold text-violet-800 dark:text-violet-200 mb-2">
              Together you'll track:
            </p>
            <ul className="space-y-1.5 text-xs text-violet-700 dark:text-violet-300">
              <li className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 shrink-0 text-violet-500" />
                Meal logs & Bite Balance Score
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 shrink-0 text-violet-500" />
                Daily health progress
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 shrink-0 text-violet-500" />
                Wellness streaks & goals
              </li>
            </ul>
          </div>

          {/* Sign-in notice for logged-out invitees */}
          {needsAuth && (
            <div className="rounded-xl border border-amber-200 dark:border-amber-900 bg-amber-50/70 dark:bg-amber-950/20 p-3 text-xs text-amber-800 dark:text-amber-200">
              Sign in or create your account to accept this invitation. We'll bring
              you right back here.
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-2.5">
            <Button
              variant="outline"
              onClick={handleReject}
              disabled={isProcessing}
              className="w-full sm:flex-1 h-10 text-sm"
            >
              {rejectInviteMutation.isPending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Declining...</>
              ) : (
                "Decline"
              )}
            </Button>
            <Button
              onClick={handleAccept}
              disabled={isProcessing}
              className="w-full sm:flex-1 h-10 text-sm bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
            >
              {needsAuth ? (
                <><LogIn className="mr-2 h-4 w-4" />Sign in to Accept</>
              ) : acceptInviteMutation.isPending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Joining...</>
              ) : (
                <><Check className="mr-2 h-4 w-4" />Accept Invitation</>
              )}
            </Button>
          </div>

          {/* Account switch option — only relevant when already signed in */}
          {!needsAuth && (
            <div className="text-center border-t border-border/50 pt-3">
              <button
                onClick={handleLogout}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-2"
              >
                <LogOut className="h-4 w-4 shrink-0" />
                Wrong account? Logout and sign in again
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InviteAcceptModal;
